import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CaretRight, Check } from '@phosphor-icons/react';
import {
  REVIEW_NOTE_MIN,
  type BriefAsset,
  type BriefDetails,
  type TaskDetail,
  type Version,
} from '@mybusinessmate/schema';
import { QA_CHECKLIST } from '../../lib/qa-checklist';
import { formatDay } from '../../lib/project-display';
import { relativeTime, TASK_TYPE_META } from '../../lib/task-display';
import { reviewsQuery, usePublishVersion, useRejectVersion, useResolveFix } from '../../lib/tasks-api';
import { toasts } from '../../lib/toast-presets';
import { answeredVersion, captionChange, versionBadge } from '../../lib/version-display';
import { PageHeader } from '../shell/page-header';
import { Icon } from '../Icon';
import { Banner } from '../ui/banner';
import { Card } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { TextArea } from '../ui/text-area';
import { Toggle } from '../ui/toggle';
import { useToast } from '../ui/toast';
import { pillClass } from '../ui/toolbar-controls';
import { AssetImage } from '../tasks/asset-image';
import { BriefPreview } from '../tasks/brief-preview';
import { ChangedChip, DeliverablePreview, SlideGrid } from '../tasks/deliverable-preview';

const PLATFORM: Record<string, string> = { instagram: 'Instagram', facebook: 'Facebook' };
const whenDay = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const whenTime = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });
const when = (iso: string) => `${whenDay.format(new Date(iso))} · ${whenTime.format(new Date(iso))}`;

/** A label over a value: the review cards' only text layout (Figma A05.6 "KV"). */
function Kv({ label, aside, children }: { label: string; aside?: ReactNode; children: ReactNode }) {
  if (!children) return null;
  return (
    <div className="flex flex-col gap-(--mbm-space-1)">
      <div className="flex items-center gap-(--mbm-space-2)">
        <dt className="text-label-small text-text-tertiary uppercase">{label}</dt>
        {aside}
      </div>
      <dd className="whitespace-pre-wrap text-body-medium text-text-primary">{children}</dd>
    </div>
  );
}

function CardHead({ eyebrow, title, subtitle }: { eyebrow: string; title: string; subtitle: string }) {
  return (
    <div className="flex flex-col gap-(--mbm-space-1)">
      <span className="text-caption font-bold tracking-wider text-text-tertiary uppercase">{eyebrow}</span>
      <h2 className="text-heading-h3 text-text-primary">{title}</h2>
      <p className="text-body-small text-text-tertiary">{subtitle}</p>
    </div>
  );
}

const hashtagLine = (tags: string[]) => tags.map((t) => `#${t}`).join(' ');
const platforms = (list: string[]) => list.map((p) => PLATFORM[p] ?? p).join(' + ');

/** Version 0, field by field: what was asked, in the order the brief form asked it. */
function BriefKv({ details, assets }: { details: BriefDetails; assets: BriefAsset[] }) {
  return (
    <>
      {details.type === 'post' || details.type === 'reel' ? (
        <dl className="flex flex-col gap-(--mbm-space-4)">
          {details.type === 'post' ? (
            <Kv label="Format">
              {[
                details.format === 'carousel' ? 'Carousel' : details.format === 'single_image' ? 'Single image' : null,
                platforms(details.platforms),
              ]
                .filter(Boolean)
                .join(' · ')}
            </Kv>
          ) : (
            <Kv label="Hook, script & shots">{details.script}</Kv>
          )}
          <Kv label="Caption">{details.caption}</Kv>
          <Kv label="Hashtags">{hashtagLine(details.hashtags)}</Kv>
          <Kv label="Publish">
            {[
              details.scheduledUploadAt ? when(details.scheduledUploadAt) : null,
              details.audioNote ? `audio: ${details.audioNote}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Kv>
        </dl>
      ) : (
        <BriefPreview details={details} />
      )}
      {assets.length > 0 ? (
        <div className="flex flex-col gap-(--mbm-space-2)">
          <span className="text-label-small text-text-tertiary uppercase">Brief assets</span>
          <ul className="flex flex-wrap gap-(--mbm-space-2)">
            {assets.map((asset) => (
              <li
                key={asset.id}
                className="relative size-(--mbm-size-asset-thumb) overflow-hidden rounded-(--mbm-radius-sm) bg-surface-field"
              >
                <AssetImage asset={asset} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}

/** The draft, field by field, with how its words compare with the brief and which notes it answers. */
function DraftKv({
  version,
  brief,
  versions,
  kind = 'draft',
}: {
  version: Version;
  brief: BriefDetails | undefined;
  versions: Version[];
  /** `live` is what the client sees now (nothing to say about changes); `fix` is a proposed edit, whose notes are its reason. */
  kind?: 'draft' | 'live' | 'fix';
}) {
  const d = version.details;
  const answered = kind === 'draft' ? answeredVersion(versions, version) : null;
  if (d.type === 'post' || d.type === 'reel') {
    const change = brief ? captionChange(brief, d) : null;
    return (
      <>
        <SlideGrid assets={version.assets} />
        <dl className="flex flex-col gap-(--mbm-space-4)">
          <Kv label="Caption" aside={change ? <ChangedChip>{change}</ChangedChip> : null}>
            {d.caption}
          </Kv>
          <Kv label="Hashtags">{hashtagLine(d.hashtags)}</Kv>
          <Kv label="Publish">
            {[d.scheduledUploadAt ? when(d.scheduledUploadAt) : null, d.audioNote ? `audio: ${d.audioNote}` : null]
              .filter(Boolean)
              .join(' · ')}
          </Kv>
          {kind === 'live' ? null : (
            <Kv
              label={kind === 'fix' ? 'Why this fix' : `Changes since v${version.versionNumber - 1}`}
              aside={
                answered ? <ChangedChip>{`Your notes on v${answered.versionNumber} addressed`}</ChangedChip> : null
              }
            >
              {version.notes ?? ''}
            </Kv>
          )}
        </dl>
      </>
    );
  }
  return (
    <>
      <DeliverablePreview version={version} />
      {kind === 'live' ? null : (
        <dl className="flex flex-col gap-(--mbm-space-4)">
          <Kv
            label={kind === 'fix' ? 'Why this fix' : 'Note from the author'}
            aside={answered ? <ChangedChip>{`Your notes on v${answered.versionNumber} addressed`}</ChangedChip> : null}
          >
            {version.notes ?? ''}
          </Kv>
        </dl>
      )}
    </>
  );
}

/** What the lead looks over (Figma "QA checklist"): ticks are theirs alone, kept nowhere, and never block an approval. */
function QaChecklist({ task }: { task: TaskDetail }) {
  const items = QA_CHECKLIST[task.taskType];
  const [ticked, setTicked] = useState<boolean[]>(() => items.map(() => false));
  return (
    <Card
      tone="panel"
      padding="none"
      className="flex flex-col gap-(--mbm-space-3) px-(--mbm-space-6) py-(--mbm-space-5-5)"
    >
      <div className="flex flex-col gap-(--mbm-space-1)">
        <h2 className="text-heading-h3 text-text-primary">QA checklist</h2>
        <p className="text-body-small text-text-tertiary">
          {TASK_TYPE_META[task.taskType].label} checklist · {ticked.filter(Boolean).length} of {items.length} ticked
        </p>
      </div>
      <ul className="flex flex-col gap-(--mbm-space-3)">
        {items.map((item, index) => (
          <li key={item} className="py-(--mbm-space-1-5)">
            <Checkbox
              label={<span className="text-body-medium text-text-primary">{item}</span>}
              checked={ticked[index]}
              onChange={(e) => setTicked((all) => all.map((t, i) => (i === index ? e.target.checked : t)))}
            />
          </li>
        ))}
      </ul>
    </Card>
  );
}

const atDay = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const atTime = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });

/** What the lead decides, in the same card for a draft and for a fix: a head, an internal note, and two buttons. */
function DecisionCard({
  title,
  subtitle,
  noteLabel,
  notes,
  onNotes,
  asked,
  children,
  actions,
}: {
  title: string;
  subtitle: string;
  noteLabel: string;
  notes: string;
  onNotes: (value: string) => void;
  /** The lead has tried to send it back: a note that is still too short now shows as an error. */
  asked: boolean;
  /** Between the note and the buttons (the client-approval switch). */
  children?: ReactNode;
  actions: ReactNode;
}) {
  const typed = notes.trim().length;
  const short = typed < REVIEW_NOTE_MIN;
  return (
    <Card
      tone="panel"
      padding="none"
      className="flex flex-col gap-(--mbm-space-3) px-(--mbm-space-6) py-(--mbm-space-5-5)"
    >
      <div className="flex flex-col gap-(--mbm-space-1)">
        <h2 className="text-heading-h3 text-text-primary">{title}</h2>
        <p className="text-body-small text-text-tertiary">{subtitle}</p>
      </div>
      <TextArea
        label={noteLabel}
        variant="filled"
        density="compact"
        rows={3}
        value={notes}
        placeholder={`Needed if you request changes (at least ${REVIEW_NOTE_MIN} characters). Only staff see it.`}
        error={asked && short ? `${noteLabel} · ${typed} of ${REVIEW_NOTE_MIN} characters minimum` : undefined}
        errorInLabel
        onChange={(e) => onNotes(e.target.value)}
      />
      {children}
      <div className="grid grid-cols-2 gap-(--mbm-space-2-5)">{actions}</div>
    </Card>
  );
}

/** "Your decision": approve and publish (or approve and finish), or send it back with a reason the author reads. */
function Decision({ task, version, onDone }: { task: TaskDetail; version: Version; onDone: () => void }) {
  const toast = useToast();
  const publish = usePublishVersion(task.id);
  const reject = useRejectVersion(task.id);
  const [notes, setNotes] = useState('');
  const [needsClient, setNeedsClient] = useState(task.requiresClientApproval);
  const [asked, setAsked] = useState(false);
  const author = version.authorName?.split(' ')[0] ?? 'the author';
  const short = notes.trim().length < REVIEW_NOTE_MIN;
  const busy = publish.isPending || reject.isPending;
  const d = version.details;
  const goes = (d.type === 'post' || d.type === 'reel') && d.scheduledUploadAt ? new Date(d.scheduledUploadAt) : null;

  const failed = () => toast(toasts.failed('Couldn’t save that', 'Please try again.'));

  const approve = () =>
    publish.mutate(
      { subId: version.id, requiresClientApproval: needsClient },
      {
        onSuccess: () => {
          toast(
            toasts.done(
              needsClient && task.clientVisible ? 'Sent to the client' : 'Published',
              `v${version.versionNumber} is out.`,
            ),
          );
          onDone();
        },
        onError: failed,
      },
    );

  const sendBack = () => {
    setAsked(true);
    if (short) return;
    reject.mutate(
      { subId: version.id, notes: notes.trim() },
      {
        onSuccess: () => {
          toast(toasts.done(`Sent v${version.versionNumber} back to ${author}`));
          onDone();
        },
        onError: failed,
      },
    );
  };

  return (
    <DecisionCard
      title="Your decision"
      subtitle={
        needsClient
          ? `Approving publishes v${version.versionNumber} to the client. Requesting changes sends it back to ${author}.`
          : `No client sign-off needed for this task. Requesting changes sends it back to ${author}.`
      }
      noteLabel={`Note for ${author} (internal)`}
      notes={notes}
      onNotes={setNotes}
      asked={asked}
      actions={
        <>
          <button
            type="button"
            disabled={busy}
            onClick={sendBack}
            className={`${pillClass('light', 'lg')} h-(--mbm-size-button-compact) justify-center`}
          >
            Request changes
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={approve}
            className={`${pillClass('dark', 'lg')} h-(--mbm-size-button-compact) justify-center`}
          >
            <Icon icon={Check} size="sm" />
            {needsClient ? 'Approve & publish to client' : 'Approve & complete task'}
          </button>
        </>
      }
    >
      <div className="flex items-center justify-between gap-(--mbm-space-4)">
        <div className="flex min-w-0 flex-col">
          <span className="text-label-medium text-text-primary">
            {needsClient ? 'Client approval needed' : 'Client approval off'}
          </span>
          <span className="text-body-xsmall text-text-tertiary">
            {needsClient
              ? goes
                ? `The client reviews it in the app. Once approved, it auto-posts ${atDay.format(goes)} at ${atTime.format(goes)}.`
                : 'The client reviews it in the app before it counts as done.'
              : goes
                ? `Approving finishes the task. The client sees it as Done and it auto-posts ${atDay.format(goes)}, ${atTime.format(goes)}.`
                : 'Approving finishes the task. The client sees it as Done.'}
          </span>
        </div>
        <Toggle checked={needsClient} onCheckedChange={setNeedsClient} aria-label="Client approval needed" />
      </div>
    </DecisionCard>
  );
}

/** A specialist's fix to what is already live: apply it quietly, or decline it with a reason. */
function FixDecision({ task, version, onDone }: { task: TaskDetail; version: Version; onDone: () => void }) {
  const toast = useToast();
  const resolve = useResolveFix(task.id);
  const [notes, setNotes] = useState('');
  const [asked, setAsked] = useState(false);
  const fix = version.pendingFix!;
  const author = fix.authorName?.split(' ')[0] ?? 'the author';
  const short = notes.trim().length < REVIEW_NOTE_MIN;
  const failed = () => toast(toasts.failed('Couldn’t save that', 'Please try again.'));

  const answer = (approve: boolean) => {
    setAsked(true);
    if (!approve && short) return;
    resolve.mutate(
      { subId: version.id, approve, ...(approve ? {} : { notes: notes.trim() }) },
      {
        onSuccess: () => {
          toast(
            toasts.done(
              approve ? 'Fix applied' : `Fix declined`,
              approve ? `v${version.versionNumber} is updated.` : `${author} is told why.`,
            ),
          );
          onDone();
        },
        onError: failed,
      },
    );
  };

  return (
    <DecisionCard
      title="Apply this fix?"
      subtitle={`Applying updates what is live quietly. The client isn’t notified, and the change is written to the activity log. Declining keeps v${version.versionNumber} as it is.`}
      noteLabel={`Note for ${author} (internal, needed if you decline)`}
      notes={notes}
      onNotes={setNotes}
      asked={asked}
      actions={
        <>
          <button
            type="button"
            disabled={resolve.isPending}
            onClick={() => answer(false)}
            className={`${pillClass('light', 'lg')} h-(--mbm-size-button-compact) justify-center`}
          >
            Decline fix
          </button>
          <button
            type="button"
            disabled={resolve.isPending}
            onClick={() => answer(true)}
            className={`${pillClass('dark', 'lg')} h-(--mbm-size-button-compact) justify-center`}
          >
            <Icon icon={Check} size="sm" />
            Apply fix to live
          </button>
        </>
      }
    />
  );
}

/**
 * The lead's review page (Figma A05.6): what was asked beside what is being sent, a checklist, and the decision. The
 * draft is the one waiting on QA; everything here is staff-only until it is published.
 */
export function ReviewPage({ task, versions }: { task: TaskDetail; versions: Version[] }) {
  const navigate = useNavigate();
  const waiting = versions.find((v) => v.internalStatus === 'pending_lead_review');
  // A specialist's fix to what is already live is answered on this same page, when no draft is waiting on QA.
  const fixOf = waiting ? undefined : versions.find((v) => v.pendingFix);
  const version = waiting ?? fixOf;
  // "1 of 5 in queue" counts along the tab this page came from: drafts, or draft fixes.
  const queue = useQuery({
    ...reviewsQuery({ tab: fixOf ? 'fixes' : 'waiting', sort: 'client_date' }),
    retry: false,
  });
  const items = queue.data?.items ?? [];
  const position = items.findIndex((i) => i.taskId === task.id);
  const next =
    items.find((i, index) => index > position && i.taskId !== task.id) ?? items.find((i) => i.taskId !== task.id);
  const type = TASK_TYPE_META[task.taskType];

  // Done with this one: on to the next in the queue, or back to the queue when it was the last.
  const leave = () =>
    next
      ? void navigate({ to: '/subdomain/admin/workbench/reviews/$taskId', params: { taskId: next.taskId } })
      : void navigate({ to: '/subdomain/admin/workbench/reviews', search: {} });

  if (!version) {
    return (
      <>
        <PageHeader title={task.title} description={`${task.businessName ?? ''} · ${task.projectName}`} />
        <Banner
          tone="info"
          title="Nothing is waiting on QA for this task"
          action={
            <Link to="/subdomain/admin/workbench/reviews" search={{}} className={pillClass('light')}>
              Back to the queue
            </Link>
          }
        >
          It has been answered, or the author hasn’t sent a version yet.
        </Banner>
      </>
    );
  }

  const brief = task.brief;
  const fix = fixOf?.pendingFix ?? null;
  // The fix, read as the version would be with it applied. Its reason stands where a draft's note would.
  const proposed = fixOf && fix ? { ...fixOf, details: fix.proposed, notes: fix.reason } : null;
  return (
    <>
      <PageHeader
        title={fixOf ? `${task.title} · draft fix` : `${task.title} · ${versionBadge(version.versionNumber)}`}
        description={(fixOf && fix
          ? [
              task.businessName,
              type.label,
              fixOf.publishedAt ? `live since ${formatDay(fixOf.publishedAt)}` : null,
              `fix proposed by ${fix.authorName ?? 'the team'} ${relativeTime(fix.createdAt)}`,
            ]
          : [
              task.businessName,
              task.projectName,
              type.label,
              `submitted by ${version.authorName ?? 'the team'} ${relativeTime(version.updatedAt)}`,
            ]
        )
          .filter(Boolean)
          .join(' · ')}
        actions={
          position >= 0 ? (
            <button type="button" disabled={!next} onClick={leave} className={pillClass('light', 'lg')}>
              {position + 1} of {items.length} in queue
              <Icon icon={CaretRight} size="xs" />
            </button>
          ) : null
        }
      />

      <div className="grid items-stretch gap-(--mbm-space-6) lg:grid-cols-2">
        <Card
          tone="panel"
          padding="none"
          className="flex flex-col gap-(--mbm-space-4) px-(--mbm-space-6) pt-(--mbm-space-5-5) pb-(--mbm-space-6)"
        >
          {fixOf ? (
            <>
              <CardHead
                eyebrow="Live now · client sees this"
                title={`Published · ${versionBadge(fixOf.versionNumber)}`}
                subtitle={
                  fixOf.publishedAt
                    ? `${fixOf.clientReviewedAt ? 'Approved by the client' : 'Sent to the client'} on ${formatDay(fixOf.clientReviewedAt ?? fixOf.publishedAt)}`
                    : 'Sent to the client'
                }
              />
              <DraftKv version={fixOf} brief={brief?.details} versions={versions} kind="live" />
            </>
          ) : (
            <>
              <CardHead
                eyebrow="What was asked"
                title="Brief · v0"
                subtitle={`Written by ${brief?.authorName ?? 'the team'} on ${formatDay(brief?.createdAt ?? task.createdAt)} on the client’s behalf`}
              />
              {brief ? <BriefKv details={brief.details} assets={brief.assets} /> : null}
            </>
          )}
        </Card>
        <Card
          tone="panel"
          padding="none"
          className="flex flex-col gap-(--mbm-space-4) px-(--mbm-space-6) pt-(--mbm-space-5-5) pb-(--mbm-space-6)"
        >
          {proposed && fix ? (
            <>
              <CardHead
                eyebrow="Proposed fix"
                title={`Fix · ${fix.fields.join(', ') || 'details'} only`}
                subtitle={`By ${fix.authorName ?? 'the team'} · client still sees the live version`}
              />
              <DraftKv version={proposed} brief={brief?.details} versions={versions} kind="fix" />
            </>
          ) : (
            <>
              <CardHead
                eyebrow="What you’re sending"
                title={`Draft · ${versionBadge(version.versionNumber)}`}
                subtitle={`By ${version.authorName ?? 'the team'} · not visible to client`}
              />
              <DraftKv version={version} brief={brief?.details} versions={versions} />
            </>
          )}
        </Card>
      </div>

      <div className="grid items-start gap-(--mbm-space-6) lg:grid-cols-2">
        <QaChecklist task={task} />
        {fixOf ? (
          <FixDecision task={task} version={fixOf} onDone={leave} />
        ) : (
          <Decision task={task} version={version} onDone={leave} />
        )}
      </div>
    </>
  );
}
